import React, { useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

import HeroSection   from './Components/home/HeroSection';
import PropertyCard  from './Components/home/Poperty/PropertyCard';
import WhyChooseUs   from './Components/home/WhyChooseUs';
import AdPopup       from './Components/home/AdPopup';
import ShortsSection from './Components/home/ShortsSection';
import AppExclusiveDeals from './Components/home/AppExclusiveDeals';
import PreferenceModal from './Components/home/PreferenceModal';

import { MyContext } from '../../Context/MyContext';
import ActivityTimeline from '../../Components/ActivityTimeline';

const WISHLIST_HOME_SECTION = {
  id: 'wishlist',
  type: 'wishlist',
  header: 'Your Wishlist',
  subheader: 'Properties you saved for later — pick up where you left off.',
  badge_label: 'Saved',
  badge_color: '#e11d48',
};

/**
 * Helper to render skeleton cards while loading
 */
const SectionSkeleton = () => (
  <div className="section-cards">
    {[1, 2, 3, 4].map(i => (
      <div key={i} className="home-skel-card">
        <div className="home-skel-img-box home-skel-line">
          <div className="home-skel-badge" />
          <div className="home-skel-heart" />
        </div>
        <div className="home-skel-body">
          <div className="home-skel-row1">
            <div className="home-skel-pill home-skel-line" />
            <div className="home-skel-loc home-skel-line" />
          </div>
          <div className="home-skel-row2">
            <div className="home-skel-price home-skel-line" />
            <div className="home-skel-area home-skel-line" />
          </div>
          <div className="home-skel-row3">
            <div className="home-skel-tag home-skel-line" />
            <div className="home-skel-tag home-skel-line" />
            <div className="home-skel-tag home-skel-line" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

/**
 * Individual Section Component to manage its own fetching
 * Memoized to prevent re-renders when other sections or context update
 */
const HomeSection = React.memo(({ section, user, wishlist, fetchPropertiesPaged, selectedCity }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);

  const fetchSectionData = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    try {
      if (section.type === "recent") {
        let recentIds = [];
        if (user?.recent_property_id?.length > 0) {
          recentIds = user.recent_property_id;
        } else {
          recentIds = JSON.parse(localStorage.getItem("guest_recent")) || [];
        }
        if (recentIds.length > 0) {
          // ✅ Fetch specifically by IDs from backend
          const res = await fetchPropertiesPaged({ ids: recentIds.join(','), limit: 8 });
          if (res?.data) setData(res?.data || []);
          else setData([]);
        } else {
          setData([]);
        }
      } else if (section.type === "wishlist") {
        const wishlistIds = Array.isArray(wishlist)
          ? wishlist.map(Number).filter(Boolean)
          : [];
        if (wishlistIds.length > 0) {
          const res = await fetchPropertiesPaged({ ids: wishlistIds.join(','), limit: 8 });
          const rows = res?.data || [];
          // Keep wishlist order when possible
          const byId = new Map(rows.map((p) => [Number(p.id), p]));
          setData(wishlistIds.map((id) => byId.get(id)).filter(Boolean));
        } else {
          setData([]);
        }
      } else {
        // Fetch specific flags from backend
        const params = { status: 2, limit: 8 };
        if (section.flag) params[section.flag] = true;
        // ONLY filter by city for "city" specific sections as per user request
        if (selectedCity?.id && section.type === 'city') params.city_id = selectedCity.id;
        
        const res = await fetchPropertiesPaged(params);
        if (res?.data) setData(res.data);
        else setData([]);
      }
    } catch (e) {
      console.error(`Error fetching home section ${section.id}:`, e);
      setData([]);
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, [
    section.id,
    section.type,
    section.flag,
    user?.recent_property_id,
    wishlist,
    fetchPropertiesPaged,
    selectedCity?.id,
  ]);

  useEffect(() => {
    fetchSectionData();
  }, [fetchSectionData]);

  // Hide entire section if no properties found as per user request
  if (!loading && data.length === 0) return null;

  return (
    <section className="home-section">
      <div className="section-header">
        <div>
          <h2 className="section-title">{section.header}</h2>
          <p className="section-sub">{section.subheader}</p>
        </div>
        {section.type === "wishlist" ? (
          <Link to="/account" state={{ activeView: "wishlist" }} className="section-view-all">
            View All →
          </Link>
        ) : section.type !== "recent" ? (
          <a 
            href={`/properties?tab=${section.flag === 'is_urgent' ? 'urgent' : section.flag === 'is_highlighted' ? 'highlighted' : section.flag === 'is_project' ? 'projects' : 'all'}${section.type === 'city' && selectedCity ? `&city_id=${selectedCity.id}` : ''}`} 
            className="section-view-all"
          >
            View All →
          </a>
        ) : null}
      </div>
      
      {loading ? (
        <SectionSkeleton />
      ) : data.length > 0 ? (
        <div className="section-cards">
          {data.map(property => (
            <PropertyCard
              key={property.id}
              property={property}
              badgeLabel={section.badge_label}
              badgeColor={section.badge_color}
            />
          ))}
        </div>
      ) : (
        <div className="no-data-msg">
          <p>No properties found {section.type === 'city' && selectedCity ? `in ${selectedCity.city_name}` : ''} for this category.</p>
        </div>
      )}
    </section>
  );
});

const Home = () => {
  const { homeSections, user, wishlist, fetchPropertiesPaged, selectedCity, preferencesComplete } = useContext(MyContext);
  const [showSuggest, setShowSuggest] = useState(false);
  const citySections = useMemo(() => {
    if (!selectedCity) return [];
    return [
      { id: 'city_properties', header: `${selectedCity.city_name} Properties`, subheader: `Explore best properties in ${selectedCity.city_name}`, type: 'city', badge_label: 'Local', badge_color: '#3b82f6' },
      { id: 'city_projects', header: `${selectedCity.city_name} Projects`, subheader: `Latest projects in ${selectedCity.city_name}`, type: 'city', flag: 'is_project', badge_label: 'Project', badge_color: '#2e7d32' },
    ];
  }, [selectedCity]);

  // Ensure wishlist section exists even when CMS home-sections omit it
  const displaySections = useMemo(() => {
    const base = Array.isArray(homeSections) ? [...homeSections] : [];
    if (base.some((s) => s?.type === 'wishlist' || s?.id === 'wishlist')) return base;
    const recentIdx = base.findIndex((s) => s?.id === 'recent' || s?.type === 'recent');
    if (recentIdx >= 0) {
      base.splice(recentIdx + 1, 0, WISHLIST_HOME_SECTION);
      return base;
    }
    return [WISHLIST_HOME_SECTION, ...base];
  }, [homeSections]);

  useEffect(() => {
    if (!preferencesComplete) {
      const timer = setTimeout(() => {
        setShowSuggest(true);
      }, 30000);
      return () => clearTimeout(timer);
    } else {
      setShowSuggest(false);
    }
  }, [preferencesComplete]);

  return (
    <div className="home-page">
      <AdPopup />
      <HeroSection />

      <PreferenceModal show={showSuggest} onClose={() => setShowSuggest(false)} />

      <main className="home-sections">
        <ActivityTimeline />
        {displaySections.length === 0 ? (
          <>
            <section className="home-section" aria-busy="true" aria-label="Loading listings">
              <div className="section-header">
                <div>
                  <div className="home-skel-line" style={{ width: 180, height: 22, marginBottom: 8, borderRadius: 6 }} />
                  <div className="home-skel-line" style={{ width: 240, height: 14, borderRadius: 6 }} />
                </div>
              </div>
              <SectionSkeleton />
            </section>
            <section className="home-section" aria-busy="true">
              <div className="section-header">
                <div>
                  <div className="home-skel-line" style={{ width: 160, height: 22, marginBottom: 8, borderRadius: 6 }} />
                  <div className="home-skel-line" style={{ width: 200, height: 14, borderRadius: 6 }} />
                </div>
              </div>
              <SectionSkeleton />
            </section>
          </>
        ) : (
          displaySections.map((section) => (
            <React.Fragment key={section.id}>
              <HomeSection
                section={section}
                user={user}
                wishlist={wishlist}
                fetchPropertiesPaged={fetchPropertiesPaged}
                selectedCity={selectedCity}
              />
              {section.id === 'recent' && citySections.length > 0 && (
                <>
                  {citySections.map((citySection) => (
                    <HomeSection
                      key={citySection.id}
                      section={citySection}
                      user={user}
                      wishlist={wishlist}
                      fetchPropertiesPaged={fetchPropertiesPaged}
                      selectedCity={selectedCity}
                    />
                  ))}
                </>
              )}
              {section.id === "highlighted" && <WhyChooseUs />}
            </React.Fragment>
          ))
        )}
      </main>

      <AppExclusiveDeals />
      <ShortsSection />
    </div>
  );
};

export default Home;
